import { useEffect, useRef } from "react";
import { getId, Icon, Modal } from "@fluentui/react";
import { StyleUtils } from "../../../common/views/StyleUtils";
import { Utils } from "../../../common/views/Utils";
import { usePaymentDetailVM } from "../../viewmodels/PaymentDetailVM";
import { PaidInvoiceInfo, PaymentType } from "../../models/PaymentModels";
import InvoiceDetailView from "../../../invoices/views/shared/InvoiceDetailView";
import { OrderModel } from "../../../orders/models/OrderModel";

type PaymentDetailProps = {
    id: string;
    onClose: () => void;
    onOrderDetail?: (order: OrderModel) => void;
}

export default function PaymentDetailView({ id, onClose, onOrderDetail }: PaymentDetailProps) {
    const hasLoaded = useRef(false);
    const vm = usePaymentDetailVM();

    useEffect(() => {
        if (hasLoaded.current) return;

        hasLoaded.current = true;

        vm.get(id);
        vm.getPictures(id);
    }, [vm, id]);

    const isMobile = window.innerWidth <= 500;
    const titleId: string = getId('title');
    const subtitleId: string = getId('subText');
    const width4 = Utils.getColumnWidth(4);
    const width6 = Utils.getColumnWidth(6);
    const width8 = Utils.getColumnWidth(8);

    const modalHeader = () => {
        return <div className="popup-header">
            <div className="ms-Grid ml--10 mr--10">
                <div className="ms-Grid-row bold">
                    <div className={width6}>
                        Payment Details
                    </div>
                    <div className={width6 + " right"}>
                        <Icon iconName="ChromeClose" className="pointer" onClick={onClose} />
                    </div>
                </div>
            </div>
        </div>;
    }

    const printRow = (label: string, value: string) => {
        return <div className="ms-Grid-row pv-5">
            <div className={width4 + " left"}>{label}:</div>
            <div className={width8 + " right"}>{value}</div>
        </div>
    }

    const defaultHeader = (label: string) => {
        return <div className="p-10 font-bold primary-background-color">
            {label}
        </div>;
    }

    const basicInfoHeader = () => {
        const payment = vm.payment;

        if (payment === undefined || payment === null) return <div></div>;

        const remaining = payment.amount - payment.applied

        return <div className="ms-Grid-row pv-10 font-bold primary-background-color">
            <div className={width6 + " left"}>Payment Info</div>
            <div className={width6 + " right"}>
                ${payment.amount.toFixed(2)} / ${remaining.toFixed(2)}
            </div>
        </div>;
    }

    const basicInfo = () => {
        const payment = vm.payment;

        if (payment === undefined || payment === null) return <div></div>;

        const remaining = payment.amount - payment.applied;
        const confirmedText = payment.isConfirmed ? "Confirmed" : "Not Confirmed Yet";
        const confirmedColor = payment.isConfirmed ? "active" : "firmed";

        return <div>
            {printRow("ID", payment.id)}
            {printRow("Type", PaymentType[payment.type])}
            {printRow("Date", Utils.formatShortDate(payment.transactionDate))}
            {printRow("Amount", "$ " + payment.amount.toFixed(2))}
            {printRow("Applied", "$ " + payment.applied.toFixed(2))}
            <div className="firmed">{printRow("Remaining", "$ " + remaining.toFixed(2))}</div>
            {printRow("Paid To", payment.account.provider + "-" + payment.account.account)}
            <div className={confirmedColor}>{printRow("Confirmed", confirmedText)}</div>

            {payment.isConfirmed && payment.confirmedDate && <>
                {printRow("Confirmed On", Utils.formatLongDate(payment.confirmedDate))}
            </>}
        </div>;
    }

    const bankInfo = () => {
        const payment = vm.payment;

        if (payment === undefined || payment === null) return <div></div>;

        return <div>
            {printRow("Transaction #", payment.transactionNo ?? "")}
            {printRow("Account #", payment.fromAccountNo ?? "")}
            {printRow("Account Name", payment.fromName ?? "")}
            {printRow("Reference #", payment.referenceNo ?? "")}
        </div>;
    }

    const onInvoiceClick = (id: string) => {
        vm.setSelectedInvoice(id);
        vm.setShowInvoice(true);
    }

    const onInvoiceClose = () => {
        vm.setSelectedInvoice("");
        vm.setShowInvoice(false);
    }

    const printInvoiceRow = (invoice: PaidInvoiceInfo, index: number) => {
        return <div
            className="ms-Grid-row pv-5 cursor"
            onClick={() => onInvoiceClick(invoice.invoiceId)}
        >
            <div className={width6}>{invoice.invoiceId}</div>
            <div className={width6 + " right"}>$ {invoice.amount.toFixed(2)}</div>
        </div>;
    }

    //const printPicture = (picture: PictureModel, index: number) => {
    //    return <div className="pv-15">
    //        {ProductUtils.getLargePicture(picture.type, picture.)}
    //    </div>;
    //}

    return <>
        <Modal
            titleAriaId={titleId}
            subtitleAriaId={subtitleId}
            isOpen={true}
            onDismiss={onClose}
            isBlocking={false}
            isModeless={false}
            styles={StyleUtils.getModalWidthStyles(450)}
        >
            {modalHeader()}

            <div
                id={subtitleId}
                className={(isMobile ? "mobile-" : "") + "popup-body"}
            >
                {vm.payment && <div className="ms-Grid ml--10 mr--10">
                    <div className="ph-10">{basicInfoHeader()}</div>
                    <div className="pt-5 ph-10">{basicInfo()}</div>

                    <div className="pt-5">{defaultHeader("Bank Info")}</div>
                    <div className="pt-5 ph-10">{bankInfo()}</div>

                    <div className="pt-5">{defaultHeader("Apply To Invoices")}</div>
                    <div className="pt-5 ph-10">
                        {vm.payment.invoices.map(printInvoiceRow)}
                    </div>
                </div>}
            </div>
        </Modal>

        {vm.showInvoice && !Utils.isNullOrEmpty(vm.selectedInvoice) && <>
            <InvoiceDetailView
                id={vm.selectedInvoice}
                onClose={onInvoiceClose}
                onOrderDetail={onOrderDetail}
            />
        </>}
    </>;
}